import { useMemo, useState } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import CompositionsLight from "skia-motion/src/CompositionsLight";
import Loading from "src/components/Loading";
import useLoading from "src/components/LoadingBar/hooks/useLoading";
import SkiaPlayer from "src/components/SkiaPlayer";
import useClearingTimeouts from "src/hooks/useClearingTimeouts";
import { useInsideModal } from "src/modals";
import InternalModal from "src/modals/configs/InternalModal";
import { ShareableResolvedProps } from "../../hooks/useShareableResolvedProps";
import { ShareableProcessingState } from "../../types";

const BasicLoading = () => {
	return (
		<View
			style={{
				flex: 1,
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<Loading />
		</View>
	);
};

const ShareablePreview = ({
	style,
	processingState,
	resolvedProps,
}: {
	style?: StyleProp<ViewStyle>;
	processingState: ShareableProcessingState;
	resolvedProps: ShareableResolvedProps;
}) => {
	const {
		props: {
			shareable: { composition },
		},
		closeSelf,
	} = useInsideModal<InternalModal["Shareable"]>();
	const { createClearingTimeout } = useClearingTimeouts();
	const { progressSV } = useLoading({
		complete: processingState === "Success",
		isActive: processingState === "Processing" || processingState === "Success",
		onAnimationComplete: () => {
			createClearingTimeout(() => {
				closeSelf();
			}, 800);
		},
		estimatedTime: 10_000,
	});

	const { shareableAspectRatio } = useMemo(() => {
		const config = CompositionsLight[composition];
		return {
			shareableWidth: config.videoConfig.width,
			shareableHeight: config.videoConfig.height,
			shareableAspectRatio: config.videoConfig.width / config.videoConfig.height,
		};
	}, [composition]);
	const [innerDim, setInnerDim] = useState<{
		width: number;
		height: number;
	} | null>(null);

	return (
		<View
			style={[
				style,
				{
					alignItems: "center",
				},
			]}
			onLayout={(event) => {
				const { height } = event.nativeEvent.layout;
				setInnerDim({
					width: height * shareableAspectRatio,
					height,
				});
			}}
		>
			<View
				style={{
					width: innerDim?.width,
					height: innerDim?.height,
				}}
			>
				<SkiaPlayer
					processingProgress={progressSV}
					composition={composition}
					props={(resolvedProps?.success ? resolvedProps?.props : null) ?? null}
					LoadingComponent={BasicLoading}
				/>
			</View>
		</View>
	);
};
export default ShareablePreview;
